/**
 * i18n веб-инбокса обращений агентов (DEV-436, фаза 2 DEV-332).
 *
 * Вынесено отдельным модулем и подмешивается в основные локали через deep-merge
 * в ./index.ts — как routingOnboarding (DEV-352). Кладёт блок agentTickets.* и
 * пункт common.navigation.agentTickets (deep-merge сливает вглубь common.navigation).
 *
 * ВАЖНО: не использовать литеральные фигурные скобки `{`/`}` в строках —
 * vue-i18n трактует их как интерполяцию и падает с SyntaxError при рендере.
 */

type LocaleOverride = Record<string, unknown>

export const agentTicketsMessages: Record<'ru' | 'en' | 'vn', LocaleOverride> = {
  ru: {
    common: { navigation: { agentTickets: 'Обращения агентов' } },
    agentTickets: {
      title: 'Обращения агентов',
      subtitle: 'Запросы агентов: возьмите в работу и ответьте',
      filters: {
        status: 'Статус',
        type: 'Тип',
        agent: 'Агент',
        agentPlaceholder: 'ID агента',
        toggle: 'Фильтры',
        from: 'С даты',
        to: 'По дату',
        assignedToMe: 'Только мои',
        apply: 'Применить',
        reset: 'Сбросить',
        all: 'Все',
      },
      columns: {
        type: 'Тип',
        agent: 'Агент',
        status: 'Статус',
        sla: 'Срок SLA',
        created: 'Создано',
      },
      status: {
        new: 'Новое',
        in_progress: 'В работе',
        closed: 'Закрыто',
      },
      type: {
        commission_question: 'Вопрос по вознаграждению',
        meeting_request: 'Запрос на встречу',
        partnership: 'Партнёрство',
        strategy_question: 'Вопрос по стратегии',
        client_registration: 'Регистрация клиента',
        other: 'Другой вопрос',
      },
      badge: {
        escalated: 'Эскалировано',
      },
      empty: {
        title: 'Обращений нет',
        description: 'Здесь появятся обращения агентов по выбранным фильтрам.',
      },
      detail: {
        breadcrumb: 'Обращение',
        loading: 'Загрузка…',
        back: 'К списку',
        agent: 'Агент',
        deal: 'Сделка',
        noDeal: 'Без сделки',
        sla: 'Срок SLA',
        created: 'Создано',
        takenBy: 'В работе у',
        responsible: 'Ответственный',
        notAssigned: 'Не назначено',
        conversationTitle: 'Переписка',
        noMessages: 'Сообщений пока нет.',
        historyTitle: 'История',
        noHistory: 'Изменений пока нет.',
        author: {
          agent: 'Агент',
          staff: 'Сотрудник',
        },
        reply: {
          placeholder: 'Введите ответ агенту…',
          send: 'Отправить',
          takeInWork: 'Взять в работу при отправке',
          hint: 'Enter — отправить, Shift+Enter — новая строка',
        },
        actions: {
          take: 'Взять в работу',
          transfer: 'Передать',
          close: 'Закрыть',
        },
        close: {
          title: 'Закрыть обращение',
          noteLabel: 'Комментарий',
          notePlaceholder: 'Необязательный комментарий при закрытии',
          cancel: 'Отмена',
          confirm: 'Закрыть',
        },
      },
      toast: {
        taken: 'Обращение взято в работу',
        transferred: 'Обращение передано',
        closed: 'Обращение закрыто',
        replied: 'Ответ отправлен',
        actionError: 'Не удалось выполнить действие',
        loadError: 'Не удалось загрузить обращение',
      },
    },
  },
  en: {
    common: { navigation: { agentTickets: 'Agent inquiries' } },
    agentTickets: {
      title: 'Agent inquiries',
      subtitle: 'Agent requests: take in work and reply',
      filters: {
        status: 'Status',
        type: 'Type',
        agent: 'Agent',
        agentPlaceholder: 'Agent ID',
        toggle: 'Filters',
        from: 'From date',
        to: 'To date',
        assignedToMe: 'Mine only',
        apply: 'Apply',
        reset: 'Reset',
        all: 'All',
      },
      columns: {
        type: 'Type',
        agent: 'Agent',
        status: 'Status',
        sla: 'SLA due',
        created: 'Created',
      },
      status: {
        new: 'New',
        in_progress: 'In progress',
        closed: 'Closed',
      },
      type: {
        commission_question: 'Commission question',
        meeting_request: 'Meeting request',
        partnership: 'Partnership',
        strategy_question: 'Strategy question',
        client_registration: 'Client registration',
        other: 'Other question',
      },
      badge: {
        escalated: 'Escalated',
      },
      empty: {
        title: 'No inquiries',
        description: 'Agent inquiries matching the selected filters will appear here.',
      },
      detail: {
        breadcrumb: 'Inquiry',
        loading: 'Loading…',
        back: 'Back to list',
        agent: 'Agent',
        deal: 'Deal',
        noDeal: 'No deal',
        sla: 'SLA due',
        created: 'Created',
        takenBy: 'Handled by',
        responsible: 'Responsible',
        notAssigned: 'Unassigned',
        conversationTitle: 'Conversation',
        noMessages: 'No messages yet.',
        historyTitle: 'History',
        noHistory: 'No changes yet.',
        author: {
          agent: 'Agent',
          staff: 'Staff',
        },
        reply: {
          placeholder: 'Type a reply to the agent…',
          send: 'Send',
          takeInWork: 'Take in work on send',
          hint: 'Enter to send, Shift+Enter for a new line',
        },
        actions: {
          take: 'Take in work',
          transfer: 'Transfer',
          close: 'Close',
        },
        close: {
          title: 'Close inquiry',
          noteLabel: 'Note',
          notePlaceholder: 'Optional note on closing',
          cancel: 'Cancel',
          confirm: 'Close',
        },
      },
      toast: {
        taken: 'Inquiry taken in work',
        transferred: 'Inquiry transferred',
        closed: 'Inquiry closed',
        replied: 'Reply sent',
        actionError: 'Could not perform the action',
        loadError: 'Could not load the inquiry',
      },
    },
  },
  vn: {
    common: { navigation: { agentTickets: 'Yêu cầu đại lý' } },
    agentTickets: {
      title: 'Yêu cầu đại lý',
      subtitle: 'Yêu cầu từ đại lý: nhận xử lý và trả lời',
      filters: {
        status: 'Trạng thái',
        type: 'Loại',
        agent: 'Đại lý',
        agentPlaceholder: 'ID đại lý',
        toggle: 'Bộ lọc',
        from: 'Từ ngày',
        to: 'Đến ngày',
        assignedToMe: 'Chỉ của tôi',
        apply: 'Áp dụng',
        reset: 'Đặt lại',
        all: 'Tất cả',
      },
      columns: {
        type: 'Loại',
        agent: 'Đại lý',
        status: 'Trạng thái',
        sla: 'Hạn SLA',
        created: 'Tạo lúc',
      },
      status: {
        new: 'Mới',
        in_progress: 'Đang xử lý',
        closed: 'Đã đóng',
      },
      type: {
        commission_question: 'Hỏi về hoa hồng',
        meeting_request: 'Yêu cầu cuộc hẹn',
        partnership: 'Hợp tác',
        strategy_question: 'Hỏi về chiến lược',
        client_registration: 'Đăng ký khách hàng',
        other: 'Câu hỏi khác',
      },
      badge: {
        escalated: 'Đã leo thang',
      },
      empty: {
        title: 'Chưa có yêu cầu',
        description: 'Yêu cầu của đại lý theo bộ lọc đã chọn sẽ hiển thị ở đây.',
      },
      detail: {
        breadcrumb: 'Yêu cầu',
        loading: 'Đang tải…',
        back: 'Về danh sách',
        agent: 'Đại lý',
        deal: 'Giao dịch',
        noDeal: 'Không có giao dịch',
        sla: 'Hạn SLA',
        created: 'Tạo lúc',
        takenBy: 'Đang xử lý bởi',
        responsible: 'Người phụ trách',
        notAssigned: 'Chưa phân công',
        conversationTitle: 'Hội thoại',
        noMessages: 'Chưa có tin nhắn.',
        historyTitle: 'Lịch sử',
        noHistory: 'Chưa có thay đổi.',
        author: {
          agent: 'Đại lý',
          staff: 'Nhân viên',
        },
        reply: {
          placeholder: 'Nhập câu trả lời cho đại lý…',
          send: 'Gửi',
          takeInWork: 'Nhận xử lý khi gửi',
          hint: 'Enter để gửi, Shift+Enter để xuống dòng',
        },
        actions: {
          take: 'Nhận xử lý',
          transfer: 'Chuyển tiếp',
          close: 'Đóng',
        },
        close: {
          title: 'Đóng yêu cầu',
          noteLabel: 'Ghi chú',
          notePlaceholder: 'Ghi chú tùy chọn khi đóng',
          cancel: 'Hủy',
          confirm: 'Đóng',
        },
      },
      toast: {
        taken: 'Đã nhận yêu cầu để xử lý',
        transferred: 'Đã chuyển yêu cầu',
        closed: 'Đã đóng yêu cầu',
        replied: 'Đã gửi trả lời',
        actionError: 'Không thể thực hiện hành động',
        loadError: 'Không thể tải yêu cầu',
      },
    },
  },
}
